import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { getProductThumbnailUrl } from '@/utils/imageUtils';

/**
 * Search result component that displays a list of search results
 * @param {Object} props
 * @param {Array} props.results - List of search result items
 * @param {boolean} props.hasMore - Whether there are more results
 * @param {Function} props.onViewAllResults - Function called when "View All Results" is clicked
 * @param {Function} props.onSelectResult - Function called when a result item is selected
 * @param {string} props.searchQuery - The current search query
 * @param {string} props.className - Additional CSS classes
 */
const SearchResultBox = ({ 
  results, 
  hasMore, 
  onViewAllResults, 
  onSelectResult,
  searchQuery,
  className 
}) => {
  const { t } = useTranslation();
  
  if (!results || results.length === 0) {
    return null;
  }

  return (
    <div className={`p-4 bg-white rounded-md shadow-[0px_4px_12px_0px_rgba(0,0,0,0.10)] outline outline-1 outline-offset-[-1px] outline-[#dddddd] flex flex-col justify-start items-start gap-4 ${className} max-h-[365px] overflow-y-auto`}>
      <div className="text-[#aaaaaa] text-sm w-full">{t('common.related_products')}</div>
      
      {results.map((item) => (
        <div 
          key={item.id} 
          className="inline-flex justify-start items-center gap-3 w-full cursor-pointer hover:bg-[#f4f4f4]"
          onClick={() => onSelectResult(item)}
        >
          <div className="w-14 h-14 relative bg-white border border-[#DDDDDD] rounded overflow-hidden">
            <img 
              className="w-14 h-14 mix-blend-darken" 
              src={getProductThumbnailUrl(item.images?.[0]?.url)} 
              alt={item.localizedName}
            />
          </div>
          <div className="flex-1 h-[42px] py-0.5 inline-flex flex-col justify-between items-start overflow-hidden">
            <div className="self-stretch h-[17px] text-[#111111] text-sm leading-[21px] truncate">{item.localizedName}</div>
            <div className="self-stretch h-[17px] opacity-50 text-[#111111] text-xs leading-[18px] truncate">
              {item.name || item.set?.localizedName || item?.brand?.localizedName}
            </div>
          </div>
        </div>
      ))}

      {hasMore && (
        <div 
          className="self-stretch h-12 px-20 py-[17px] border-t border-[#eeeeee] inline-flex justify-center items-center gap-2.5 cursor-pointer hover:bg-[#f4f4f4]"
          onClick={onViewAllResults}
        >
          <div className="text-[#2966ff] text-[13px]">{t('common.view_all_results')}</div>
        </div>
      )}
    </div>
  );
};

export default SearchResultBox; 
